import { useState, useEffect, useRef } from "react";
import { Check, Pencil } from "lucide-react";

/**
 * EditableField — affichage en lecture par défaut, édition au clic / double-clic.
 * Sauvegarde automatique au blur ou après 600ms d'inactivité.
 *
 * props: label, value, onSave(value), type="text"|"date"|"textarea"|"select"|"number", placeholder, options(for select)
 */
export default function EditableField({ label, value, onSave, type = "text", placeholder = "—", className = "", testid, options }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => { setV(value ?? ""); }, [value]);

  const persist = async (next) => {
    const norm = type === "number" ? (next === "" ? 0 : parseFloat(next) || 0) : next;
    const cur = type === "number" ? (value === "" || value == null ? 0 : value) : (value ?? "");
    if (norm === cur) return;
    setSaving(true);
    try {
      await onSave(norm);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1000);
    } finally { setSaving(false); }
  };

  const onChange = (e) => {
    const nv = e.target.value;
    setV(nv);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (type === "select") {
      persist(nv);
      setEditing(false);
      return;
    }
    timerRef.current = setTimeout(() => persist(nv), 600);
  };

  const finish = async () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    await persist(v);
    setEditing(false);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && type !== "textarea") { e.preventDefault(); finish(); }
    if (e.key === "Escape") { setV(value ?? ""); setEditing(false); }
  };

  const displayValue = (() => {
    if (value === "" || value == null) return null;
    if (type === "select" && options) {
      const opt = options.find((o) => o.value === value);
      return opt?.label || value;
    }
    if (type === "date") return new Date(value).toLocaleDateString("fr-FR");
    if (type === "number") return typeof value === "number" ? value.toLocaleString("fr-FR") : value;
    return value;
  })();

  return (
    <div className={`group ${className}`}>
      {label && <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold flex items-center gap-1">
        {label}
        {savedFlash && <Check size={10} className="text-emerald-600"/>}
      </div>}
      {!editing ? (
        <div
          data-testid={testid}
          onClick={() => setEditing(true)}
          onDoubleClick={() => setEditing(true)}
          className="mt-0.5 text-sm text-stone-900 cursor-text hover:bg-stone-50 rounded px-1 -mx-1 py-0.5 min-h-[26px] flex items-center gap-2"
        >
          {displayValue ? <span className={type === "textarea" ? "whitespace-pre-wrap" : ""}>{displayValue}</span> : <span className="text-stone-400 italic">{placeholder}</span>}
          <Pencil size={10} className="text-stone-300 opacity-0 group-hover:opacity-100"/>
        </div>
      ) : type === "select" ? (
        <select
          autoFocus
          value={v}
          onChange={onChange}
          onBlur={() => setEditing(false)}
          data-testid={`${testid}-input`}
          className="mt-0.5 w-full border border-stone-900/40 focus:border-stone-900 outline-none rounded px-2 py-1 text-sm bg-white"
        >
          {(options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : type === "textarea" ? (
        <textarea
          autoFocus
          value={v}
          onChange={onChange}
          onBlur={finish}
          onKeyDown={onKey}
          rows={3}
          data-testid={`${testid}-input`}
          className="mt-0.5 w-full border border-[#EB1F10]/40 focus:border-[#EB1F10] outline-none rounded px-2 py-1 text-sm bg-white"
        />
      ) : (
        <input
          autoFocus
          type={type === "number" ? "number" : type}
          step={type === "number" ? "any" : undefined}
          value={v}
          onChange={onChange}
          onBlur={finish}
          onKeyDown={onKey}
          data-testid={`${testid}-input`}
          className="mt-0.5 w-full border border-[#EB1F10]/40 focus:border-[#EB1F10] outline-none rounded px-2 py-1 text-sm bg-white"
        />
      )}
      {saving && <div className="text-[10px] text-stone-400 mt-0.5">Sauvegarde…</div>}
    </div>
  );
}
